import { usePostHog } from 'posthog-js/react';

/**
 * Custom hook for PostHog analytics tracking
 * Provides easy-to-use methods for tracking user events
 */
export const useAnalytics = () => {
  const posthog = usePostHog();

  const trackEvent = (eventName, properties = {}) => {
    if (posthog) {
      posthog.capture(eventName, {
        timestamp: new Date().toISOString(),
        ...properties
      });
      
      // Log in development for debugging
      if (process.env.NODE_ENV === 'development') {
        console.log('📊 PostHog Event:', eventName, properties);
      }
    }
  };

  const identifyUser = (userId, userProperties = {}) => {
    if (posthog) {
      posthog.identify(userId, userProperties);
    }
  };

  const trackPageView = (pageName, properties = {}) => {
    trackEvent('page_view', {
      page: pageName,
      ...properties
    });
  };

  const trackClick = (element, additionalProps = {}) => {
    trackEvent('click', {
      element,
      ...additionalProps
    });
  };

  const trackProjectView = (projectTitle, projectId) => {
    trackEvent('project_viewed', {
      project_title: projectTitle,
      project_id: projectId
    });
  };

  const trackBlogRead = (blogId, blogTitle, blogSlug) => {
    // Session-based deduplication key
    const sessionKey = `blog_read_${blogId}_${posthog?.get_session_id() || 'unknown'}`;
    
    // Check if already tracked in this session
    if (typeof window !== 'undefined' && window.sessionStorage) {
      if (window.sessionStorage.getItem(sessionKey)) {
        console.log(`🔄 Blog ${blogId} already tracked in this session, skipping duplicate`);
        return;
      }
      // Mark as tracked in this session
      window.sessionStorage.setItem(sessionKey, Date.now().toString());
    }
    
    trackEvent('blog_read', {
      blog_id: blogId,
      blog_title: blogTitle,
      blog_slug: blogSlug,
      timestamp: new Date().toISOString(),
      session_id: posthog?.get_session_id() || 'unknown'
    });
    
    console.log(`📊 Blog read tracked: ID=${blogId}, Title="${blogTitle}"`);
  };

  const trackContactInteraction = (type, value) => {
    trackEvent('contact_interaction', {
      interaction_type: type,
      value: value
    });
  };

  const trackSocialClick = (platform, url) => {
    trackEvent('social_click', {
      platform,
      url
    });
  };

  const trackCVDownload = () => {
    trackEvent('cv_download', {
      download_time: new Date().toISOString()
    });
  };

  const trackNavigation = (fromSection, toSection) => {
    trackEvent('navigation', {
      from_section: fromSection,
      to_section: toSection
    });
  };

  const trackBlogEngagement = (blogId, blogSlug, engagementType, additionalData = {}) => {
    trackEvent('blog_engagement', {
      blog_id: blogId,
      blog_slug: blogSlug,
      engagement_type: engagementType,
      timestamp: new Date().toISOString(),
      ...additionalData
    });
  };

  return {
    trackEvent,
    identifyUser,
    trackPageView,
    trackClick,
    trackProjectView,
    trackBlogRead,
    trackContactInteraction,
    trackSocialClick,
    trackCVDownload,
    trackNavigation,
    trackBlogEngagement,
    posthog
  };
};

export default useAnalytics;